import { ClientTable } from "../../../components/ui/ClientTable";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { FormSelect } from "../../../components/ui/select";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, ShieldAlert } from 'lucide-react';
import { AppLayout, PageBackLink } from '../../../components/layout';
import { LeaveActionToast } from '../../leave-management/components/LeaveActionToast';
import '../inventory.css';
import {
  loadWarehouseCatalog,
  fetchWarehouseCount,
  fetchWarehouseTypes,
  formatCurrency,
  displayWarehouseType,
} from '../../../services/warehouseService';

const TOAST_KEY = 'warehouse_list_toast';
const PAGE_SIZE = 50;

function statusLabel(status) {
  if (status === 'Active') return i18n.t('status.active');
  if (status === 'Inactive') return i18n.t('status.inactive');
  return status;
}

function WarehouseStatusBadge({ status }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{
        backgroundColor: status === 'Active' ? '#ecfdf5' : '#f1f5f9',
        color: status === 'Active' ? '#065f46' : '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {statusLabel(status)}
    </span>
  );
}

function WarehouseMobileCard({ warehouse, typeLabel, onEdit, onConfigure }) {
  const isInactive = warehouse.status === 'Inactive';
  const isPrimary = warehouse.primaryHub === 'Yes';

  return (
    <div
      className="p-4 space-y-3"
      style={{ backgroundColor: isInactive ? '#fafafa' : undefined }}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <h4
            className="font-semibold text-foreground leading-snug"
            style={{ color: isInactive ? '#64748b' : undefined }}
          >
            {warehouse.name}
          </h4>
          {isPrimary && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{
                backgroundColor: '#E8EEF7',
                color: '#192B56',
                letterSpacing: '0.2px',
              }}
            >
              <Trans i18nKey="inventory.hub" />
            </span>
          )}
        </div>
        <WarehouseStatusBadge status={warehouse.status} />
      </div>

      <div className="text-xs space-y-2 per-file-hint">
        <div className="flex justify-between items-start gap-3">
          <span className="shrink-0">{i18n.t('inventory.warehouseCode')}:</span>
          <span className="font-medium text-foreground font-mono text-right break-all">
            {warehouse.code || i18n.t('inventory.emDash')}
          </span>
        </div>
        <div className="flex justify-between items-start gap-3">
          <span className="shrink-0">{i18n.t('inventory.warehouseType')}:</span>
          <span className="font-medium text-foreground text-right">
            {typeLabel || i18n.t('inventory.emDash')}
          </span>
        </div>
        <div className="flex justify-between items-start gap-3">
          <span className="shrink-0">{i18n.t('inventory.warehouseManager')}:</span>
          <span className="font-medium text-foreground text-right">
            {warehouse.manager || i18n.t('inventory.unassigned')}
          </span>
        </div>
        <div className="flex justify-between items-center gap-3 pt-1">
          <span className="shrink-0">{i18n.t('inventory.skusHeldColumn')}:</span>
          <span className="font-semibold text-foreground tabular-nums">
            {warehouse.skusHeld || 0}
          </span>
        </div>
        <div className="flex justify-between items-center gap-3">
          <span className="shrink-0">{i18n.t('inventory.stockValue')}:</span>
          <span className="font-semibold text-foreground tabular-nums text-right">
            {formatCurrency(warehouse.stockValue || 0)}
          </span>
        </div>
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-(--color-text-border)/25">
        <span className="text-[11px] font-semibold per-file-hint uppercase tracking-wide">
          <Trans i18nKey="common.actions" />
        </span>
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="text-[13px] font-semibold text-primary cursor-pointer bg-transparent border-0 p-0"
            onClick={() => onConfigure(warehouse)}
          >
            <Trans i18nKey="inventory.configure" />
          </button>
          <button
            type="button"
            className="text-[13px] font-semibold text-primary cursor-pointer bg-transparent border-0 p-0"
            onClick={() => onEdit(warehouse)}
          >
            <Trans i18nKey="common.edit" />
          </button>
        </div>
      </div>
    </div>
  );
}

function WarehouseEmptyState({ isFiltersActive, onAddWarehouse }) {
  return (
    <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
      <Building2 size={40} style={{ color: '#94a3b8' }} />
      <h4 style={{ margin: 0, color: '#334155', fontWeight: '600', fontSize: '15px' }}>
        {isFiltersActive
          ? i18n.t('inventory.noWarehousesMatchFilters')
          : i18n.t('inventory.noWarehousesConfigured')}
      </h4>
      <p style={{ margin: 0, color: '#64748b', fontSize: '13px', maxWidth: '320px', lineHeight: '1.5' }}>
        {isFiltersActive
          ? i18n.t('inventory.clearTypeStatusFiltersHint')
          : i18n.t('inventory.addWarehouseEmptyHint')}
      </p>
      {!isFiltersActive && (
        <button
          type="button"
          className="btn-primary"
          onClick={onAddWarehouse}
          style={{ marginTop: '4px', borderRadius: '6px', backgroundColor: '#152273' }}
        >
          <Plus size={16} />
          <Trans i18nKey="inventory.addWarehouse" />
        </button>
      )}
    </div>
  );
}

export default function WarehouseList() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    const storedToast = sessionStorage.getItem(TOAST_KEY);
    if (storedToast) {
      setToastMessage(storedToast);
      sessionStorage.removeItem(TOAST_KEY);
    }
  }, []);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  useEffect(() => {
    let cancelled = false;
    fetchWarehouseTypes()
      .then((types) => {
        if (!cancelled) setAvailableTypes((types || []).filter((t) => t.isActive !== false));
      })
      .catch(() => {
        if (!cancelled) setAvailableTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadWarehouses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadWarehouseCatalog({
        filterTypeId: selectedTypeId,
        filterStatus: selectedStatus,
        page: 0,
        size: PAGE_SIZE,
      });
      setWarehouses(result.warehouses || []);
    } catch (err) {
      setWarehouses([]);
      setError(err?.message || i18n.t('inventory.failedToLoadWarehouses'));
    } finally {
      setLoading(false);
    }
  }, [selectedTypeId, selectedStatus]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    let cancelled = false;
    fetchWarehouseCount()
      .then((count) => {
        if (!cancelled) setTotalCount(count);
      })
      .catch(() => {
        if (!cancelled) setTotalCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouses]);

  const isFiltersActive = selectedTypeId !== 'All' || selectedStatus !== 'All';
  const hasAnyWarehouses =
    !loading &&
    !error &&
    warehouses.length === 0 &&
    !isFiltersActive &&
    (totalCount === 0 || totalCount === null);

  const onAddWarehouse = () => navigate('/IMS/inventory/locations/new');
  const onEditWarehouse = (warehouse) => navigate(`/IMS/inventory/locations/${warehouse.id}/edit`);
  const onConfigureWarehouse = (warehouse) =>
    navigate(`/IMS/inventory/locations/${warehouse.id}/configure`);

  return (
    <AppLayout>
      <div className="inventory-module">
        {toastMessage && (
          <LeaveActionToast
            toast={{ message: toastMessage, type: 'success' }}
            onClose={() => setToastMessage(null)}
          />
        )}

        {/* HEADER SECTION */}
        <div className="content-header page-header-responsive" style={{ alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <PageTitleRow back={<PageBackLink to="/" />}>
              <h1 className="page-title"><Trans i18nKey="inventory.warehousesTitle" /></h1>
            </PageTitleRow>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={onAddWarehouse}
            style={{ borderRadius: '6px', padding: '10px 20px', fontWeight: '600', backgroundColor: '#152273' }}
          >
            <Plus size={16} />
            <Trans i18nKey="inventory.addWarehouse" />
          </button>
        </div>

        {/* EDGE CASE: NO WAREHOUSES IN SYSTEM AT ALL */}
        {hasAnyWarehouses && (
          <div
            style={{
              backgroundColor: '#fffbe6',
              border: '1px solid #ffe58f',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              color: '#d48806'
            }}
          >
            <ShieldAlert size={22} style={{ flexShrink: 0, marginTop: '2px', color: '#d48806' }} />
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: '#b78103' }}>
                <Trans i18nKey="inventory.noWarehousesConfiguredRestriction" />
              </h4>
              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: '#8c6300' }}>
                <Trans i18nKey="inventory.noWarehousesConfiguredHint" />
              </p>
            </div>
          </div>
        )}

        {/* FILTERS BAR */}
        <div className="inv-filter-panel" style={{ marginBottom: '18px' }}>
          <div className="inv-filter-fields" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                <Trans i18nKey="common.type" />
              </label>
              <FormSelect
                className="filter-select"
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                style={{ width: '180px', borderRadius: '8px', height: '38px', borderColor: '#cbd5e1' }}
              >
                <option value="All">{i18n.t('common.allTypes')}</option>
                {availableTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </FormSelect>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                <Trans i18nKey="status.status" />
              </label>
              <FormSelect
                className="filter-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{ width: '180px', borderRadius: '8px', height: '38px', borderColor: '#cbd5e1' }}
              >
                <option value="All">{i18n.t('common.allStatuses')}</option>
                <option value="Active">{i18n.t('status.active')}</option>
                <option value="Inactive">{i18n.t('status.inactive')}</option>
              </FormSelect>
            </div>

            {isFiltersActive && (
              <button
                type="button"
                className="btn-clear-filters"
                onClick={() => {
                  setSelectedTypeId('All');
                  setSelectedStatus('All');
                }}
                style={{ marginTop: '20px', height: '38px' }}
              >
                <Trans i18nKey="formActions.clearFilters" />
              </button>
            )}
          </div>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '14px 18px',
              marginBottom: '18px',
              color: '#991b1b',
              fontSize: '13px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <span>{error}</span>
            <button
              type="button"
              className="btn-secondary"
              onClick={loadWarehouses}
              style={{ height: '32px', padding: '0 14px', fontSize: '12px' }}
            >
              <Trans i18nKey="attendance.retry" />
            </button>
          </div>
        )}

        {/* TABLE / MOBILE CARDS */}
        <div
          className="table-section borderless-table-section"
          style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
        >
          <div className="table-wrapper hidden md:block">
            <ClientTable className="leads-table">
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                    <Trans i18nKey="inventory.warehouseColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                    <Trans i18nKey="inventory.codeColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                    <Trans i18nKey="inventory.typeColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                    <Trans i18nKey="inventory.managerColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
                    <Trans i18nKey="inventory.statusColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'center' }}>
                    <Trans i18nKey="inventory.skusHeldColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.stockValueColumn" />
                  </th>
                  <th style={{ padding: '14px 20px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textAlign: 'right' }}>
                    <Trans i18nKey="inventory.actionsColumn" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '48px 20px', color: '#64748b' }}>
                      <Trans i18nKey="inventory.loadingWarehouses" />
                    </td>
                  </tr>
                ) : warehouses.length > 0 ? (
                  warehouses.map((wh) => {
                    const isInactive = wh.status === 'Inactive';
                    const isPrimary = wh.primaryHub === 'Yes';

                    return (
                      <tr
                        key={wh.id}
                        // className="clickable-row"
                        // onClick={() => navigate(`/IMS/inventory/locations/${wh.id}`)}
                        style={{
                          backgroundColor: isInactive ? '#fafafa' : '#ffffff',
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <td style={{ padding: '14px 20px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '700', color: isInactive ? '#64748b' : '#0f172a', fontSize: '14px' }}>
                              {wh.name}
                            </span>
                            {isPrimary && (
                              <span
                                style={{
                                  backgroundColor: '#E8EEF7',
                                  color: '#192B56',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Trans i18nKey="inventory.hub" />
                              </span>
                            )}
                          </div>
                        </td>

                        <td style={{ padding: '14px 20px', color: '#64748b', fontSize: '13px', fontFamily: 'monospace' }}>
                          {wh.code}
                        </td>

                        <td style={{ padding: '14px 20px', color: '#64748b', fontSize: '13px' }}>
                          {displayWarehouseType(wh, availableTypes) || i18n.t('inventory.emDash')}
                        </td>

                        <td style={{ padding: '14px 20px', color: '#64748b', fontSize: '13px' }}>
                          {wh.manager || i18n.t('inventory.emDash')}
                        </td>

                        <td style={{ padding: '14px 20px' }}>
                          <span
                            style={{
                              backgroundColor: wh.status === 'Active' ? '#dcfce7' : '#f1f5f9',
                              color: wh.status === 'Active' ? '#15803d' : '#64748b',
                              fontSize: '12px',
                              fontWeight: '600',
                              padding: '4px 10px',
                              borderRadius: '16px',
                              display: 'inline-block'
                            }}
                          >
                            {statusLabel(wh.status)}
                          </span>
                        </td>

                        <td style={{ padding: '14px 20px', textAlign: 'center', fontWeight: '600', color: '#334155' }}>
                          {wh.skusHeld || 0}
                        </td>

                        <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>
                          {formatCurrency(wh.stockValue || 0)}
                        </td>

                        <td
                          style={{ padding: '14px 20px', textAlign: 'right' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
                            <button
                              type="button"
                              onClick={() => onConfigureWarehouse(wh)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#2563eb',
                                fontWeight: '600',
                                fontSize: '13px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}
                              title={i18n.t('inventory.configure')}
                            >
                              <Trans i18nKey="inventory.configure" />
                            </button>
                            <button
                              type="button"
                              onClick={() => onEditWarehouse(wh)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#2563eb',
                                fontWeight: '600',
                                fontSize: '13px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}
                              title={i18n.t('inventory.editWarehouse')}
                            >
                              <Trans i18nKey="common.edit" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '48px 20px' }}>
                      <WarehouseEmptyState
                        isFiltersActive={isFiltersActive}
                        onAddWarehouse={onAddWarehouse}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </ClientTable>
          </div>

          <div className="md:hidden">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: '#64748b', fontSize: '14px' }}>
                <Trans i18nKey="inventory.loadingWarehouses" />
              </div>
            ) : warehouses.length > 0 ? (
              <div className="divide-y divide-(--color-text-border)/35">
                {warehouses.map((warehouse) => (
                  <WarehouseMobileCard
                    key={warehouse.id}
                    warehouse={warehouse}
                    typeLabel={displayWarehouseType(warehouse, availableTypes)}
                    onEdit={onEditWarehouse}
                    onConfigure={onConfigureWarehouse}
                  />
                ))}
              </div>
            ) : (
              <WarehouseEmptyState
                isFiltersActive={isFiltersActive}
                onAddWarehouse={onAddWarehouse}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
